import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop — Global route-change scroll restoration.
 *
 * Resets the window scroll position to (0, 0) every time the pathname
 * changes. Mounted once inside the Router (sibling to <Routes>) so it
 * applies to ALL routes without per-page wiring.
 *
 * Notes:
 *   * Only triggers on pathname changes — querystring + hash changes
 *     (e.g. filter pills that update `?status=pending`) intentionally
 *     do NOT reset scroll, so in-page interactions stay where they are.
 *   * Browser back/forward (POP) is honored by the router itself and
 *     this still fires — clients expect "new page = top of page".
 *   * `instant` jumps avoid the smooth-scroll perception lag that makes
 *     the new page feel slow on slower devices.
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Some browsers (Safari iOS) honor scrollRestoration='manual' implicitly;
    // explicitly disable so a back-nav doesn't restore the previous scroll
    // and fight our reset.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch (_) {
      // Some older browsers reject `behavior: instant` — fall back to default.
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}
